<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleStatusTransitionContent from './PostSaleStatusTransitionContent.vue';
import {
  defaultTransitionForm,
  initialStatus,
  transitionFormPayload,
} from './postSaleStatusTransitionForm';
import {
  deliveryForecastApiValue,
  maskDeliveryForecast,
  statusRequiresDeliveryForecast,
} from './postSaleStatusDeadline';
import { transitionFormErrors } from './postSaleStatusTransitionValidation';

const props = defineProps({
  catalog: { type: Object, default: () => ({}) },
  caseData: { type: Object, required: true },
  agendaAction: { type: String, default: '' },
  saving: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
  statusOptions: { type: Array, required: true },
});

const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();
const selectedStatus = ref('');
const form = reactive(defaultTransitionForm());
const errors = ref({});
const deliveryForecastRequired = computed(() =>
  statusRequiresDeliveryForecast(selectedStatus.value)
);

watch(
  () => [props.agendaAction, props.caseData, props.show],
  () => {
    selectedStatus.value = initialStatus({
      agendaAction: props.agendaAction,
      status: props.caseData?.status,
    });
    Object.assign(form, defaultTransitionForm(props.caseData));
    errors.value = {};
  },
  { immediate: true }
);

watch(selectedStatus, () => {
  errors.value = {};
  if (selectedStatus.value !== props.caseData?.status) form.status_due_at = '';
});

watch(
  form,
  () => {
    errors.value = {};
  },
  { deep: true }
);

const submit = () => {
  if (!selectedStatus.value) return;
  errors.value = transitionFormErrors(selectedStatus.value, form);
  if (Object.keys(errors.value).length) return;

  emit('submit', {
    status: selectedStatus.value,
    status_due_at: deliveryForecastApiValue(form.status_due_at) || undefined,
    reason: form.reason || undefined,
    payload: transitionFormPayload(selectedStatus.value, form),
  });
};

const updateDeliveryForecast = event => {
  form.status_due_at = maskDeliveryForecast(event.target.value);
};
</script>

<template>
  <LaisDrawer
    :show="show"
    side="bottom"
    :title="t('POST_SALE.TRANSITION.TITLE')"
    :close-label="t('POST_SALE.ACTIONS.CLOSE')"
    @close="emit('close')"
  >
    <template #body>
      <PostSaleStatusTransitionContent
        v-model="selectedStatus"
        :catalog="catalog"
        :delivery-forecast-required="deliveryForecastRequired"
        :errors="errors"
        :form="form"
        :post-sale-case-id="caseData.id"
        :status-options="statusOptions"
        @update-delivery-forecast="updateDeliveryForecast"
        @update-reason="form.reason = $event"
      />
    </template>
    <template #footer>
      <div class="flex justify-end gap-2">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('POST_SALE.ACTIONS.CANCEL') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :is-loading="saving"
          :disabled="!selectedStatus"
          @click="submit"
        >
          {{ t('POST_SALE.ACTIONS.UPDATE_STATUS') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisDrawer>
</template>
